import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Facebook, Instagram, Linkedin } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import translations from '../data/translations';
import { LOGO, CONTACT } from '../data/siteData';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { lang, setLang } = useLang();
  const t = translations[lang].nav;
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location]);

  const navLinks = [
    { to: '/', label: t.home },
    { to: '/#services', label: t.services, isHash: true },
    { to: '/portfolio', label: t.portfolio },
    { to: '/knowledge', label: t.knowledge },
    { to: '/contact', label: t.contact },
  ];

  const handleNavClick = (e, link) => {
    if (link.isHash) {
      e.preventDefault();
      const id = link.to.replace('/#', '');
      if (location.pathname !== '/') {
        window.location.href = '/#' + id;
        return;
      }
      const element = document.getElementById(id);
      if (element) {
        // Use scrollIntoView with block: 'start' - CSS scroll-margin-top handles the offset
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <header
      data-testid="header"
      className={`nav-header ${scrolled ? 'scrolled' : ''}`}
    >
      <div className="container">
        <div className="flex items-center justify-between">
          {/* Logo - Original colors, no filters */}
          <Link 
            to="/" 
            className="flex-shrink-0" 
            data-testid="header-logo"
          >
            <img 
              src={LOGO} 
              alt="Imperguel" 
              className="h-10 md:h-11"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={(e) => handleNavClick(e, link)}
                className={`nav-link ${location.pathname === link.to ? 'active text-[#C9A86C]' : ''}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Right Side */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Social Icons */}
            <div className="flex items-center gap-3" data-testid="social-icons">
              <a 
                href={CONTACT.facebook} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-white/50 hover:text-[#C9A86C] transition-colors duration-200"
                data-testid="header-facebook"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a 
                href={CONTACT.instagram} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-white/50 hover:text-[#C9A86C] transition-colors duration-200"
                data-testid="header-instagram"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a 
                href={CONTACT.linkedin} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-white/50 hover:text-[#C9A86C] transition-colors duration-200"
                data-testid="header-linkedin"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
            
            {/* Divider */}
            <div className="w-px h-4 bg-white/20" />
            
            {/* Language Selector */}
            <div className="flex items-center gap-1 text-xs font-medium" data-testid="lang-selector">
              <button 
                onClick={() => setLang('pt')} 
                className={`px-1.5 py-1 transition-colors duration-200 ${
                  lang === 'pt' ? 'text-[#C9A86C]' : 'text-white/50 hover:text-white'
                }`} 
                data-testid="lang-pt"
              >
                PT
              </button>
              <span className="text-white/30">/</span>
              <button 
                onClick={() => setLang('en')} 
                className={`px-1.5 py-1 transition-colors duration-200 ${
                  lang === 'en' ? 'text-[#C9A86C]' : 'text-white/50 hover:text-white'
                }`} 
                data-testid="lang-en"
              >
                EN
              </button>
            </div>
            
            {/* CTA Button */}
            <Link 
              to="/contact" 
              data-testid="header-cta" 
              className="btn-primary"
            >
              <span>{t.cta}</span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-button"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div 
          className="lg:hidden absolute top-full left-0 right-0 bg-[#111114]/98 backdrop-blur-xl border-b border-white/10" 
          data-testid="mobile-menu"
        >
          <nav className="container py-6">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={(e) => handleNavClick(e, link)}
                  className={`py-2 text-base font-light ${
                    location.pathname === link.to 
                      ? 'text-[#C9A86C]' 
                      : 'text-white/80'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Mobile Language */}
              <div className="flex items-center gap-4 py-2 border-t border-white/10 mt-2 pt-4">
                <button 
                  onClick={() => setLang('pt')} 
                  className={`text-sm font-light ${lang === 'pt' ? 'text-[#C9A86C]' : 'text-white/60'}`}
                >
                  Português
                </button>
                <button 
                  onClick={() => setLang('en')} 
                  className={`text-sm font-light ${lang === 'en' ? 'text-[#C9A86C]' : 'text-white/60'}`}
                >
                  English
                </button>
              </div>
              
              {/* Mobile CTA */}
              <Link to="/contact" className="btn-primary mt-2 text-center">
                {t.cta}
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
